import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ShieldCheck, ChevronDown } from "lucide-react";
import { SocConsole } from "@/components/SocConsole";
import { ComplianceRow } from "@/components/ComplianceRow";
import { IMAGES } from "@/content/services";

const ROTATING = ["before attackers do", "before your auditors do", "before your board hears it", "before it costs you downtime"];

const CREDS = ["OSCP", "CISSP", "CEH", "CISA", "ISO 27001 Lead Auditor", "AWS / Azure Security"];

const rise = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export const HomeHero = () => {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ROTATING.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      className="relative isolate flex min-h-[88svh] items-center overflow-hidden lg:min-h-[92vh]"
      data-testid="page-hero"
    >
      <img
        src={IMAGES.codeScreens}
        alt="Cybersecurity operations centre with live threat monitoring screens"
        width="1920"
        height="1080"
        fetchPriority="high"
        decoding="async"
        className="hero-photo absolute inset-0 -z-10 h-full w-full object-cover opacity-45"
        loading="eager"
      />
      <div className="absolute inset-0 -z-10 bg-[rgba(11,15,26,0.88)] lg:bg-[rgba(11,15,26,0.78)]" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0B0F1A]/70 via-[#0B0F1A]/40 to-[#0B0F1A]" />
      <div className="glow-orb -left-40 top-1/4 h-[420px] w-[420px] bg-[#8E9AEA]/10" aria-hidden />
      <div className="glow-orb -right-24 bottom-0 h-[380px] w-[380px] bg-[#4C5A8C]/20" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-5 pb-14 pt-28 sm:px-8 sm:pb-16 sm:pt-32 lg:px-16 lg:pb-20 lg:pt-36">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <div className="min-w-0">
            <motion.div {...rise(0.05)}>
              <span className="pill-badge" data-testid="hero-eyebrow-pill">
                <ShieldCheck className="h-3.5 w-3.5 text-[#8E9AEA]" />
                Offensive testing &amp; 24/7 security operations
              </span>
            </motion.div>

            <motion.h1
              {...rise(0.15)}
              className="mt-5 font-display text-[1.7rem] font-extrabold leading-[1.15] tracking-tight text-white sm:mt-6 sm:text-[2.15rem] md:text-[2.5rem] lg:text-[2.7rem] lg:leading-[1.12] xl:text-[3rem]"
              data-testid="hero-title"
            >
              Find what an attacker would find
              <span className="mt-1.5 block min-h-[2.5em] sm:mt-2 sm:min-h-[1.35em]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={ROTATING[i]}
                    initial={{ opacity: 0, y: "0.5em" }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: "-0.5em" }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="block text-[#8E9AEA]"
                    data-testid="hero-rotating-phrase"
                  >
                    {ROTATING[i]}.
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.h1>

            <motion.p
              {...rise(0.3)}
              className="mt-5 max-w-xl text-[15px] leading-relaxed text-[#DDE3F2] sm:text-base"
              data-testid="hero-description"
            >
              Lanvaus is an engineer-led security team running penetration testing, managed detection and response,
              and enterprise security architecture — with every finding mapped to the frameworks you are audited
              against, so security work doubles as audit evidence.
            </motion.p>

            <motion.div {...rise(0.42)} className="mt-7 cta-row">
              <Link to="/contact" className="btn-primary" data-testid="hero-primary-cta">
                Book a 30-min technical call
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/services" className="btn-secondary" data-testid="hero-secondary-cta">
                Explore Our Services
              </Link>
            </motion.div>

            <motion.div
              {...rise(0.55)}
              className="mt-7 border-t border-white/10 pt-5 sm:mt-8"
              data-testid="hero-credentials"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A6B0C9]">
                Certified practitioners
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {CREDS.map((c) => (
                  <span
                    key={c}
                    className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#DDE3F2] sm:text-xs"
                    data-testid={`hero-cred-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="min-w-0"
          >
            <SocConsole />
          </motion.div>
        </div>

        <ComplianceRow className="mt-10 sm:mt-12" compact />

        <motion.a
          href="#services-overview"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("services-overview")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="mt-8 hidden items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#A6B0C9] transition-colors duration-200 hover:text-white lg:inline-flex"
          data-testid="hero-scroll-cue"
        >
          <motion.span
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex"
          >
            <ChevronDown className="h-4 w-4" />
          </motion.span>
          What we do
        </motion.a>
      </div>
    </section>
  );
};
